import React from "react";
import {FieldValue, RegisterOptions} from "react-hook-form";
import * as EmailValidator from 'email-validator';
import FormInputValidator from "@ui/form/FormInputValidator";
import has from "lodash/has";
import get from "lodash/get";
import FormInputStyleProperties from "@ui/form/FormInputStyleProperties";
import {useTranslation} from "@ui/i18n/Translation";
import Utilities from "@ui/utils/Utilities";
import _uniqueId from 'lodash/uniqueId';
import {Validate} from "react-hook-form";

type Props = {
    isRequired?: boolean,
    placeholder?: string,
    form?: any,
    name: string,
    label?: string,
    labelHint?: string,
    disabled?: boolean,
    className?: string,
    extraClassName?: string,
    type?: 'text' | 'date' | 'number' | 'password'
    validation?: 'email' | 'phone' | FormInputValidator | Validate<any, FieldValue<any>>,
    hideErrorMessage?: boolean,
    min?: string,
    maxLength?: number
} & FormInputStyleProperties

const FormInputText: React.FunctionComponent<Props> = (
    {
        form,
        placeholder,
        name,
        isRequired = false,
        label,
        isLoading,
        disabled = false,
        className,
        extraClassName,
        labelHint,
        type = 'text',
        validation,
        hideErrorMessage = false,
        min,
        maxLength
    }) => {

    const {t} = useTranslation("common.form.error");
    const hasError = !!form && has(form.formState.errors, name);
    const errorMessage = hasError ? (get(form.formState.errors, name + ".message") || t("required")) : '';

    const options: Partial<RegisterOptions> = {
        required: isRequired,
    }
    if (!!validation) {
        if (typeof validation === "function") {
            options.validate = validation
        } else if (typeof validation === "object") {
            options.validate = {
                checkUrl: (value) => {
                    return (validation as FormInputValidator).check(value)
                }
            }
        } else if (validation === 'email')
            options.validate = value => EmailValidator.validate(value) || t("email");
    }
    let pattern: any = undefined;
    switch (type) {
        case 'date': {
            pattern = '\d{4}-\d{2}-\d{2}';
            break;
        }
        default:
            pattern = undefined

    }

    const uniqueId = _uniqueId('input-');

    const renderField = () => {
        return <>
            {!!form && <input id={uniqueId} type={type}
                              maxLength={maxLength}
                              className={Utilities.classNames(
                                  !!className ? className : "input",
                                  Utilities.NVL(hasError, 'input-error'),
                                  Utilities.NVL(isLoading, 'input-isLoading'),
                                  Utilities.NVL(disabled, 'input-disabled'),
                                  Utilities.NVL(!!extraClassName, extraClassName!)
                              )}
                              disabled={disabled}
                              pattern={pattern}
                              min={min}
                              placeholder={placeholder} {...form.register(name, options)}/>}
            {!form && <input id={uniqueId} name={name} type={type}
                             maxLength={maxLength}
                             className={Utilities.classNames(
                                 !!className ? className : "input",
                                 Utilities.NVL(hasError, 'input-error'),
                                 Utilities.NVL(isLoading, 'input-isLoading'),
                                 Utilities.NVL(disabled, 'input-disabled'),
                                 Utilities.NVL(!!extraClassName, extraClassName!)
                             )}
                             placeholder={placeholder}
                             disabled={disabled}
                             min={min}
                             pattern={type === 'date' ? '\d{4}-\d{2}-\d{2}' : undefined}/>}

        </>
    }

    return !!label ? <div>
        <div className="flex justify-between">
            <label htmlFor={uniqueId} className="label">{label}</label>
            {!!labelHint ? <span className="text-xs leading-6 text-gray-500">{labelHint}</span> : ''}
        </div>
        <div className="mt-0.5">
            {renderField()}
        </div>
        {!!errorMessage && !hideErrorMessage && <p className="help help-error">{errorMessage}</p>}
    </div>: renderField()

}

export default FormInputText;
